import { last, sortBy } from 'lodash';
import { linkParents } from './MultiLevelTableUtils';

const pathKey = (path) => path.join(' > ');

/**
 * Fold the flat purchase taxonomy ({ id, path }) into the node shape MultiLevelTableSelector
 * consumes: { value, label, path, selected, children? }. Parents' `selected` is derived from
 * their children so a stored parent id and its stored children agree. (PHOB-1889)
 */
export function buildPurchaseAffinitiesTree(categoriesMap, selectedIds = []) {
  const selected = new Set(selectedIds.map(String));
  const nodesByKey = new Map();
  const roots = [];
  const known = new Set();

  const categories = sortBy(
    Object.values(categoriesMap || {}).filter((c) => Array.isArray(c?.path) && c.path.length > 0),
    (c) => c.path.length
  );

  categories.forEach((category) => {
    const id = String(category.id);
    const parentKey = pathKey(category.path.slice(0, -1));
    const parent = category.path.length === 1 ? null : nodesByKey.get(parentKey);
    if (category.path.length > 1 && !parent) return; // orphan: taxonomy guarantees intermediates
    known.add(id);
    const node = { value: id, label: last(category.path), path: category.path, selected: selected.has(id) };
    nodesByKey.set(pathKey(category.path), node);
    if (parent) {
      parent.children = parent.children || [];
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const finalize = (nodes) =>
    sortBy(nodes, (n) => n.label.toLowerCase()).map((n) => {
      if (!n.children) return n;
      const children = finalize(n.children);
      return { ...n, children, selected: children.length > 0 && children.every((c) => c.selected) };
    });

  const missingIds = [...selected].filter((id) => !known.has(id));
  const tree = finalize(roots);
  // MultiLevelTableUtils.getUpdatedTree walks [...node.parent_ids, node.value] from the roots, so
  // every consumer links parents after building (see CommercialsSegmentCriteriaPage).
  tree.forEach((root) => linkParents(root));
  return { tree, missingIds };
}

/** Every node with selected === true, depth-first, parents before children. */
export function collectSelectedCategories(tree) {
  const out = [];
  const walk = (nodes) =>
    (nodes || []).forEach((n) => {
      if (n.selected) out.push({ id: n.value, path: n.path });
      if (n.children) walk(n.children);
    });
  walk(tree);
  return out;
}
